import { FC, ReactNode, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Autocomplete, Box, Button, ButtonBase, Dialog, IconButton, Menu, MenuItem, TextField, Typography, useMediaQuery, useTheme,
} from '@mui/material';
import {
  Close, EditOutlined, MoreHoriz, WarningAmber, PlaceOutlined, NearMeOutlined, ContentCopy,
  MenuBookOutlined, ReceiptLongOutlined, EventOutlined,
  Warehouse as WarehouseIcon, BuildOutlined,
} from '@mui/icons-material';
import type { Barn } from '../../services/barnsService';
import type { Appointment } from './types';
import { getAppointmentSubject } from './subjectLabel';
import { barnAddressLines, homePlace } from './AppointmentHoverCard';
import { AppointmentStatus, normalizeStatus, statusStyle } from './appointmentStatus';
import {
  SectionLabel, Segmented, WhenFields, ProviderPicker, ProviderOption, TypeSelect, StatusChips, useProviderAvailability,
} from './scheduleFormParts';

const cardSx = { p: 1.75, border: '1px solid #E5E7EB', borderRadius: 2.5 } as const;
const overlineSx = { fontSize: '0.68rem', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#6B7280' } as const;

/** Whole years from a calendar-day birth date (date part only, so no zone can move it). */
const ageInYears = (dob?: string | null) => {
  if (!dob) return null;
  const [y, m, d] = dob.slice(0, 10).split('-').map(Number);
  if (!y) return null;
  const now = new Date();
  const beforeBirthday = now.getMonth() + 1 < m || (now.getMonth() + 1 === m && now.getDate() < d);
  return now.getFullYear() - y - (beforeBirthday ? 1 : 0);
};

const Shell: FC<{ open: boolean; onClose: () => void; children: ReactNode; label: string }> = ({ open, onClose, children, label }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={isMobile}
      maxWidth={false}
      PaperProps={{
        'aria-label': label,
        sx: { width: 560, maxWidth: '100%', height: { sm: 'calc(100% - 64px)' }, maxHeight: { sm: 900 }, display: 'flex', flexDirection: 'column', borderRadius: { sm: 3 } },
      }}
    >
      {children}
    </Dialog>
  );
};

const ActionButton: FC<{ icon: ReactNode; label: string; onClick: () => void; disabled?: boolean; title?: string }> = ({
  icon, label, onClick, disabled, title,
}) => (
  <Button
    onClick={onClick}
    disabled={disabled}
    title={title}
    variant="outlined"
    startIcon={icon}
    sx={{ height: 46, borderRadius: 2.5, textTransform: 'none', fontWeight: 600, color: '#3B2E1A', borderColor: '#D1D5DB' }}
  >
    {label}
  </Button>
);

export interface PanelCan {
  edit: boolean;
  remove: boolean;
  generateInvoice: boolean;
  openPatientChart: boolean;
}

/**
 * The appointment panel (Schedule canvas board "Appointment panel"): what the front desk needs
 * first, then the actions. Times are the CLINIC's (formatInstant); Appointment.date is an instant.
 */
export const AppointmentPanel: FC<{
  appointment: Appointment;
  onClose: () => void;
  visitMembers: Appointment[];
  onOpenAppointment: (appointment: Appointment) => void;
  barn?: Barn | null;
  /** The barn the horse lives at, for a visit at the clinic. */
  homeBarn?: Barn | null;
  typeColor: string;
  formatInstant: (value: string, pattern: string) => string;
  can: PanelCan;
  hasInvoice: boolean;
  onGenerateInvoice: () => void;
  onStatusChange: (status: AppointmentStatus) => void;
  onEdit: () => void;
  onDelete: () => void;
  onCancelAppointment: () => void;
  /** Rendered instead of the details while editing. */
  editor?: ReactNode;
}> = ({
  appointment: apt, onClose, visitMembers, onOpenAppointment, barn, homeBarn, typeColor, formatInstant, can, hasInvoice,
  onGenerateInvoice, onStatusChange, onEdit, onDelete, onCancelAppointment, editor,
}) => {
  const navigate = useNavigate();
  const [moreAnchor, setMoreAnchor] = useState<HTMLElement | null>(null);
  const [copied, setCopied] = useState(false);
  const status = normalizeStatus(apt.status);
  const cancelled = status === 'cancelled';
  const subject = getAppointmentSubject(apt);
  const minutes = apt.duration || 60;
  const endISO = new Date(new Date(apt.date).getTime() + minutes * 60_000).toISOString();
  const horse = apt.horse;
  const owners = Array.isArray(horse?.owners) ? horse!.owners! : [];
  const age = ageInYears(horse?.dateOfBirth);
  const visitIndex = visitMembers.findIndex((m) => m.id === apt.id);
  const location = apt.location?.toLowerCase();
  const placeKind = apt.barnId || barn ? 'Barn call' : location === 'farm' ? 'Farm call' : location === 'clinic' ? 'At the clinic' : apt.location;
  const addressLines = barn ? barnAddressLines(barn) : [];
  const home = homePlace(apt, homeBarn);
  // Directions use the saved address, else the place's own text (a typed location may be the address).
  const mapQuery =
    addressLines.join(', ') || barn?.name || (location === 'farm' && !apt.barnId ? horse?.location ?? '' : '') || home?.mapQuery || '';
  const alerts = (horse?.medicalAlerts ?? [])
    .map((a: any) => ({ text: typeof a === 'string' ? a : a?.text, critical: !!a?.isCritical }))
    .filter((a: { text?: string }) => !!a.text)
    .sort((a: { critical: boolean }, b: { critical: boolean }) => Number(b.critical) - Number(a.critical));

  const contacts: Array<{ role: string; name: string; phone?: string | null }> = [];
  if (barn?.contactName || barn?.contactPhone) contacts.push({ role: 'Point of contact', name: barn.contactName || barn.name, phone: barn.contactPhone });
  owners.slice(0, 2).forEach((o) => contacts.push({ role: 'Owner', name: o.name, phone: o.phone }));
  if (!horse && apt.client) contacts.push({ role: 'Owner', name: apt.client.name, phone: apt.client.phone });

  const durationText = minutes < 60 ? `${minutes} min` : minutes % 60 ? `${Math.floor(minutes / 60)}h ${minutes % 60}m` : `${minutes / 60} hr`;
  const style = statusStyle(apt.status);

  if (editor) {
    return (
      <Shell open onClose={onClose} label="Edit appointment">
        {editor}
      </Shell>
    );
  }

  return (
    <Shell open onClose={onClose} label="Appointment details">
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2.5, py: 1.75, borderBottom: '1px solid #F0F1F3' }}>
        <Typography sx={{ flex: 1, minWidth: 0, fontSize: '0.82rem', color: 'text.secondary' }} noWrap>
          {visitMembers.length > 1
            ? `${apt.barn?.name || barn?.name || 'Barn'} visit · horse ${visitIndex + 1} of ${visitMembers.length}`
            : 'Appointment'}
        </Typography>
        {can.edit && !cancelled && (
          <Button variant="outlined" size="small" startIcon={<EditOutlined />} onClick={onEdit} sx={{ textTransform: 'none', fontWeight: 600, color: '#3B2E1A', borderColor: '#E5E7EB', height: 36 }}>
            Edit
          </Button>
        )}
        {can.remove && (
          <>
            <IconButton aria-label="More actions" onClick={(e) => setMoreAnchor(e.currentTarget)} sx={{ border: '1px solid #E5E7EB', borderRadius: 2, width: 36, height: 36 }}>
              <MoreHoriz fontSize="small" />
            </IconButton>
            <Menu anchorEl={moreAnchor} open={!!moreAnchor} onClose={() => setMoreAnchor(null)}>
              <MenuItem
                onClick={() => {
                  setMoreAnchor(null);
                  onDelete();
                }}
                sx={{ color: 'error.main' }}
              >
                Delete permanently
              </MenuItem>
            </Menu>
          </>
        )}
        <IconButton aria-label="Close" onClick={onClose} sx={{ width: 36, height: 36 }}>
          <Close fontSize="small" />
        </IconButton>
      </Box>

      <Box sx={{ flex: 1, overflowY: 'auto', p: 2.5, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box sx={{ display: 'flex', gap: 1.75, alignItems: 'center' }}>
          <Box sx={{ flex: 'none', width: 56, height: 56, borderRadius: 3.5, bgcolor: '#EFE7DC', color: '#3B2E1A', fontSize: '1.4rem', fontWeight: 700, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
            {subject.name.charAt(0).toUpperCase()}
          </Box>
          <Box sx={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Typography component="h2" sx={{ m: 0, fontSize: '1.6rem', lineHeight: 1.15, fontWeight: 700, letterSpacing: '-0.02em', color: '#3B2E1A' }}>
              {subject.name}
            </Typography>
            <Typography sx={{ fontSize: '0.82rem', color: 'text.secondary' }}>
              {[
                [horse?.breed, horse?.gender].filter(Boolean).join(' '),
                age != null ? `${age} yrs` : '',
                horse?.patientId,
                owners[0] ? `Owner ${owners[0].name}` : subject.kind === 'owner' ? 'Owner visit, no patient' : subject.kind === 'barn' ? 'Barn visit, no patient' : '',
              ].filter(Boolean).join(' · ')}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
              <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.6, px: 1, py: 0.25, borderRadius: 999, bgcolor: `${typeColor}1F`, color: '#3B2E1A', fontSize: '0.75rem', fontWeight: 600 }}>
                <Box component="span" sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: typeColor }} />
                {apt.type}
              </Box>
              {placeKind && (
                <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.25, borderRadius: 999, bgcolor: '#F6EFE6', color: '#7A4314', fontSize: '0.75rem', fontWeight: 600 }}>
                  {(apt.barnId || barn) && <WarehouseIcon sx={{ fontSize: 13 }} />}
                  {placeKind}
                </Box>
              )}
            </Box>
          </Box>
        </Box>

        {can.edit ? (
          <StatusChips value={status} onChange={onStatusChange} options={cancelled ? ['cancelled', 'scheduled'] : undefined} />
        ) : (
          <Box component="span" sx={{ alignSelf: 'flex-start', display: 'inline-flex', alignItems: 'center', gap: 0.75, px: 1.5, height: 30, borderRadius: 999, bgcolor: style.fill, color: style.ink, fontSize: '0.82rem', fontWeight: 600 }}>
            <Box component="span" sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: style.dot }} />
            {style.label}
          </Box>
        )}

        {alerts.map((a: { text: string; critical: boolean }, i: number) => (
          <Box key={i} role="alert" sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start', px: 1.5, py: 1.25, borderRadius: 2.5, bgcolor: a.critical ? '#FEF1EF' : '#FFF8EB', border: '1px solid', borderColor: a.critical ? '#F6C8C1' : '#F3DDB0', color: a.critical ? '#8A1F13' : '#7A4314' }}>
            <WarningAmber sx={{ fontSize: 18, mt: '1px' }} />
            <Typography sx={{ fontSize: '0.85rem' }}>
              <strong>Medical alert:</strong> {a.text}
            </Typography>
          </Box>
        ))}

        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1.5 }}>
          <Box sx={cardSx} data-testid="appointment-detail-datetime">
            <Typography sx={overlineSx}>When</Typography>
            <Typography sx={{ fontSize: '0.95rem', fontWeight: 600 }}>{formatInstant(apt.date, 'EEE, MMM d')}</Typography>
            <Typography sx={{ fontSize: '0.82rem', color: '#374151' }}>
              {`${formatInstant(apt.date, 'h:mm a')} - ${formatInstant(endISO, 'h:mm a')} · ${durationText}`}
            </Typography>
          </Box>
          <Box sx={cardSx}>
            <Typography sx={overlineSx}>Provider</Typography>
            <Typography sx={{ fontSize: '0.95rem', fontWeight: 600 }}>{apt.assignedVet?.name || 'Unassigned'}</Typography>
            {apt.assignedVet?.professionalTitle && (
              <Typography sx={{ fontSize: '0.82rem', color: '#374151' }}>{apt.assignedVet.professionalTitle}</Typography>
            )}
          </Box>
        </Box>

        <Box component="section" aria-label="Location" sx={{ ...cardSx, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
          <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start' }}>
            <Box sx={{ flex: 'none', width: 32, height: 32, borderRadius: 2, bgcolor: '#F6EFE6', color: '#7A4314', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
              <PlaceOutlined sx={{ fontSize: 17 }} />
            </Box>
            <Box>
              <Typography sx={{ fontSize: '0.95rem', fontWeight: 600 }}>
                {barn?.name || apt.barn?.name || (location === 'farm' ? horse?.location || 'Farm call' : location === 'clinic' ? 'At the clinic' : apt.location)}
              </Typography>
              {addressLines.map((line) => (
                <Typography key={line} sx={{ fontSize: '0.85rem', color: '#374151' }}>{line}</Typography>
              ))}
              {(apt.barnId || barn) && !barn && (
                <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>Address needs a connection</Typography>
              )}
              {home && (
                <>
                  <Typography sx={{ fontSize: '0.85rem', color: '#374151', mt: 0.5 }}>Lives at {home.name}</Typography>
                  {home.addressLines.map((line) => (
                    <Typography key={line} sx={{ fontSize: '0.85rem', color: '#374151' }}>{line}</Typography>
                  ))}
                </>
              )}
            </Box>
          </Box>
          {barn?.notes && (
            <Typography sx={{ px: 1.25, py: 1, borderRadius: 2, bgcolor: '#FAFAFB', border: '1px solid #F0F1F3', fontSize: '0.82rem', lineHeight: 1.45 }}>
              <strong>Barn notes:</strong> {barn.notes}
            </Typography>
          )}
          {mapQuery && (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                component="a"
                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(mapQuery)}`}
                target="_blank"
                rel="noopener noreferrer"
                variant="outlined"
                size="small"
                startIcon={<NearMeOutlined />}
                sx={{ textTransform: 'none', fontWeight: 600, color: '#3B2E1A', borderColor: '#D1D5DB', height: 36 }}
              >
                Directions
              </Button>
              <Button
                variant="outlined"
                size="small"
                startIcon={<ContentCopy />}
                onClick={() => {
                  navigator.clipboard?.writeText(mapQuery).then(() => setCopied(true), () => undefined);
                }}
                sx={{ textTransform: 'none', fontWeight: 500, color: '#3B2E1A', borderColor: '#E5E7EB', height: 36 }}
              >
                {copied ? 'Copied' : 'Copy address'}
              </Button>
            </Box>
          )}
        </Box>

        {contacts.length > 0 && (
          <Box component="section" aria-label="Contacts" sx={{ border: '1px solid #E5E7EB', borderRadius: 2.5 }}>
            {contacts.map((c, i) => (
              <Box key={`${c.role}-${c.name}`} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 1.75, py: 1.5, borderTop: i ? '1px solid #F0F1F3' : 0 }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={overlineSx}>{c.role}</Typography>
                  <Typography sx={{ fontSize: '0.88rem', fontWeight: 600, mt: 0.25 }}>{c.name}</Typography>
                  {c.phone && <Typography sx={{ fontSize: '0.85rem', color: '#374151' }}>{c.phone}</Typography>}
                </Box>
              </Box>
            ))}
          </Box>
        )}

        {!cancelled && (
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1 }}>
            {can.openPatientChart && apt.horseId && (
              <ActionButton icon={<MenuBookOutlined />} label="Open chart" onClick={() => navigate(`/patients/${apt.horseId}`)} />
            )}
            {/* A MONEY write reached from the Schedule: billing.controller.ts `generateFromAppointment`
                is manage:charges, not manage:appointments. */}
            {can.generateInvoice && (
              <ActionButton
                icon={<ReceiptLongOutlined />}
                label={hasInvoice ? 'Invoice created' : 'Create invoice'}
                onClick={onGenerateInvoice}
                disabled={hasInvoice || !apt.horseId}
                title={hasInvoice ? 'Invoice already generated for this appointment' : !apt.horseId ? 'Invoices are per-patient; this booking has no patient' : undefined}
              />
            )}
          </Box>
        )}

        {visitMembers.length > 1 && (
          <Box component="section" aria-label="Same barn visit" sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography component="h3" sx={{ ...overlineSx, m: 0 }}>Same visit · {apt.barn?.name || barn?.name}</Typography>
              <ButtonBase onClick={() => navigate(`/barn-visits/${apt.visitGroupId}`)} sx={{ fontSize: '0.82rem', fontWeight: 600, color: '#7A4314' }}>
                Open barn visit
              </ButtonBase>
            </Box>
            <Box sx={{ border: '1px solid #E5E7EB', borderRadius: 2.5, overflow: 'hidden' }}>
              {visitMembers.map((m, i) => (
                <ButtonBase
                  key={m.id}
                  onClick={() => onOpenAppointment(m)}
                  aria-current={m.id === apt.id ? 'true' : undefined}
                  sx={{ width: '100%', display: 'flex', alignItems: 'center', gap: 1.25, px: 1.75, py: 1.25, justifyContent: 'flex-start', textAlign: 'left', bgcolor: m.id === apt.id ? '#FFFBF6' : 'transparent', borderTop: i ? '1px solid #F0F1F3' : 0 }}
                >
                  <Typography sx={{ width: 64, flex: 'none', fontSize: '0.82rem', color: 'text.secondary' }}>{formatInstant(m.date, 'h:mm a')}</Typography>
                  <Typography sx={{ fontSize: '0.88rem', fontWeight: 600 }}>{m.horse?.name ?? getAppointmentSubject(m).name}</Typography>
                  <Typography sx={{ flex: 1, minWidth: 0, fontSize: '0.82rem', color: 'text.secondary' }} noWrap>{m.type}</Typography>
                  {m.id === apt.id && <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#7A4314' }}>Viewing</Typography>}
                </ButtonBase>
              ))}
            </Box>
          </Box>
        )}

        {(apt.equipmentSchedules?.length ?? 0) > 0 && (
          <Box component="section" aria-label="Equipment" sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography component="h3" sx={{ ...overlineSx, m: 0 }}>Equipment</Typography>
            {apt.equipmentSchedules!.map((es) => (
              <Box key={es.id} sx={{ display: 'flex', alignItems: 'center', gap: 1.25, px: 1.75, py: 1.25, border: '1px solid #E5E7EB', borderRadius: 2.5 }}>
                <BuildOutlined sx={{ fontSize: 16, color: '#6B7280' }} />
                <Typography sx={{ flex: 1, fontSize: '0.88rem', fontWeight: 600 }}>{es.equipment.name}</Typography>
                <Typography sx={{ fontSize: '0.82rem', color: 'text.secondary' }}>
                  {`${formatInstant(es.startTime, 'h:mm a')} - ${formatInstant(es.endTime, 'h:mm a')}`}
                </Typography>
              </Box>
            ))}
          </Box>
        )}

        {apt.notes && (
          <Box component="section" aria-label="Notes" sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography component="h3" sx={{ ...overlineSx, m: 0 }}>Notes</Typography>
            <Typography sx={{ px: 1.75, py: 1.5, fontSize: '0.88rem', lineHeight: 1.5, bgcolor: '#FAFAFB', border: '1px solid #F0F1F3', borderRadius: 2.5, whiteSpace: 'pre-wrap' }}>
              {apt.notes}
            </Typography>
          </Box>
        )}
      </Box>

      {can.edit && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2.5, py: 1.75, borderTop: '1px solid #F0F1F3' }}>
          {cancelled ? (
            <Typography sx={{ flex: 1, fontSize: '0.82rem', color: 'text.secondary' }}>
              Cancelled. It stays on record and in the cancellations report.
            </Typography>
          ) : (
            <>
              <Button variant="outlined" startIcon={<EventOutlined />} onClick={onEdit} sx={{ textTransform: 'none', fontWeight: 600, color: '#3B2E1A', borderColor: '#D1D5DB', height: 40 }}>
                Reschedule
              </Button>
              <Box sx={{ flex: 1 }} />
              {apt.invoiceId ? (
                <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary', textAlign: 'right' }}>
                  Invoiced. Void the invoice to cancel.
                </Typography>
              ) : (
                <Button color="error" onClick={onCancelAppointment} title="Keeps it on record and in the cancellations report" sx={{ textTransform: 'none', fontWeight: 600, height: 40 }}>
                  Cancel appointment
                </Button>
              )}
            </>
          )}
        </Box>
      )}
    </Shell>
  );
};

export interface EditForm {
  date: string;
  duration: number;
  type: string;
  location: string;
  notes: string;
  assignedVetId: string;
  status: AppointmentStatus;
  /** A barn booking with no horses can move to another barn ('' otherwise). */
  barnId: string;
}

/** The edit form (Schedule canvas board "Edit appointment"); its state and save live in the page. */
export const AppointmentEditor: FC<{
  appointment: Appointment;
  form: EditForm;
  onChange: (patch: Partial<EditForm>) => void;
  providers: ProviderOption[];
  types: Array<{ name: string; color: string }>;
  barn?: Barn | null;
  /** The equipment picker, owned by the page (it tracks which reservations were touched). */
  equipment: ReactNode;
  onSave: () => void;
  onCancel: () => void;
  /** The live horses of its barn visit (empty when it is not on one). */
  visitHorseIds: string[];
  /** Barns it may move to; given only for a barn booking with no horses. */
  barnChoices?: Barn[];
  offline: boolean;
}> = ({ appointment: apt, form, onChange, providers, types, barn, equipment, onSave, onCancel, visitHorseIds, barnChoices, offline }) => {
  // Two or more horses edit together, so the visit's own horses never read as busy.
  const isVisitMember = visitHorseIds.length > 1;
  const start = form.date ? new Date(form.date) : null;
  const busy = useProviderAvailability(start, form.duration, isVisitMember ? visitHorseIds : [apt.id]);
  const subject = getAppointmentSubject(apt);
  const barnLinked = !!(apt.barnId || apt.barn);
  const shownBarn = barnChoices?.find((b) => b.id === form.barnId) ?? barn;

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2.5, py: 2, borderBottom: '1px solid #F0F1F3' }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography component="h2" sx={{ m: 0, fontSize: '1.1rem', fontWeight: 700, color: '#3B2E1A' }}>Edit appointment</Typography>
          <Typography sx={{ fontSize: '0.82rem', color: 'text.secondary' }} noWrap>
            {[subject.name, isVisitMember ? `${apt.barn?.name || barn?.name || 'Barn'} visit` : ''].filter(Boolean).join(' · ')}
          </Typography>
        </Box>
        <IconButton aria-label="Close without saving" onClick={onCancel} sx={{ width: 36, height: 36 }}>
          <Close fontSize="small" />
        </IconButton>
      </Box>

      <Box sx={{ flex: 1, overflowY: 'auto', p: 2.5, display: 'flex', flexDirection: 'column', gap: 2.75 }}>
        <Box>
          <SectionLabel>Who</SectionLabel>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 1.5, py: 1.25, border: '1px solid #D1D5DB', borderRadius: 2.5 }}>
            <Box sx={{ flex: 'none', width: 36, height: 36, borderRadius: 2.5, bgcolor: '#EFE7DC', color: '#3B2E1A', fontWeight: 700, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
              {subject.name.charAt(0).toUpperCase()}
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: '0.88rem', fontWeight: 600 }}>
                {subject.name}
                {apt.horse?.patientId && <Box component="span" sx={{ fontWeight: 400, color: 'text.secondary' }}>{` · ${apt.horse.patientId}`}</Box>}
              </Typography>
              <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>
                {[apt.horse?.owners?.[0] ? `Owner ${apt.horse.owners[0].name}` : '', apt.barn?.name || barn?.name || ''].filter(Boolean).join(' · ') || 'To change who it is for, book a new appointment.'}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box>
          <SectionLabel>When</SectionLabel>
          <WhenFields
            start={start}
            minutes={form.duration}
            onChange={(next, minutes) => onChange({ date: next ? next.toISOString() : form.date, duration: minutes })}
          />
        </Box>

        <Box>
          <SectionLabel>Provider · free at this time?</SectionLabel>
          <ProviderPicker providers={providers} value={form.assignedVetId} onChange={(id) => onChange({ assignedVetId: id })} busy={busy} />
        </Box>

        <Box>
          <SectionLabel>Type</SectionLabel>
          <TypeSelect types={types} value={form.type} onChange={(type) => onChange({ type })} />
        </Box>

        <Box>
          <SectionLabel>Where</SectionLabel>
          {barnLinked ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, p: 1.5, border: '1px solid #D1D5DB', borderRadius: 2.5 }}>
              {barnChoices && barnChoices.length > 0 ? (
                <Autocomplete
                  options={barnChoices}
                  value={barnChoices.find((b) => b.id === form.barnId) ?? null}
                  onChange={(_e, value) => value && onChange({ barnId: value.id })}
                  getOptionLabel={(option) => option.name}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  disabled={offline}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      inputProps={{ ...params.inputProps, 'aria-label': 'Barn' }}
                      helperText={offline ? 'Changing the barn needs a connection.' : undefined}
                    />
                  )}
                />
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <WarehouseIcon sx={{ fontSize: 16, color: '#7A4314' }} />
                  <Typography sx={{ fontSize: '0.88rem', fontWeight: 600 }}>{apt.barn?.name || barn?.name}</Typography>
                </Box>
              )}
              {shownBarn && barnAddressLines(shownBarn).length > 0 && (
                <Typography sx={{ pl: 3, fontSize: '0.82rem', color: '#374151' }}>{barnAddressLines(shownBarn).join(', ')}</Typography>
              )}
              {(shownBarn?.contactName || shownBarn?.contactPhone) && (
                <Typography sx={{ pl: 3, fontSize: '0.82rem', color: '#374151' }}>
                  {`Contact: ${[shownBarn.contactName, shownBarn.contactPhone].filter(Boolean).join(' · ')}`}
                </Typography>
              )}
            </Box>
          ) : (
            <Segmented
              label="Location"
              value={form.location === 'farm' ? 'farm' : 'clinic'}
              options={[{ value: 'clinic', label: 'Clinic' }, { value: 'farm', label: 'Farm call' }]}
              onChange={(location) => onChange({ location })}
            />
          )}
        </Box>

        <Box>
          <SectionLabel>Equipment</SectionLabel>
          {equipment}
        </Box>

        <Box>
          <SectionLabel>Status</SectionLabel>
          <StatusChips value={form.status} onChange={(status) => onChange({ status })} />
        </Box>

        <TextField
          label="Notes"
          value={form.notes}
          onChange={(e) => onChange({ notes: e.target.value })}
          multiline
          minRows={3}
          fullWidth
        />
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2.5, py: 1.75, borderTop: '1px solid #F0F1F3' }}>
        <Typography sx={{ flex: 1, fontSize: '0.78rem', color: 'text.secondary' }}>
          {isVisitMember ? 'Time, length and vet change every horse in this visit. Type, status, notes and equipment are this horse only.' : ''}
        </Typography>
        <Button variant="outlined" onClick={onCancel} sx={{ textTransform: 'none', fontWeight: 600, color: '#3B2E1A', borderColor: '#D1D5DB', height: 40 }}>
          Cancel
        </Button>
        <Button variant="contained" onClick={onSave} disabled={!start || !form.type.trim()} sx={{ textTransform: 'none', fontWeight: 600, boxShadow: 'none', height: 40 }}>
          Save changes
        </Button>
      </Box>
    </>
  );
};
